import { BL_STATUS, PACKAGING_TYPE } from '../enums/bl.enum';
import { DELIVERY_DETAIL_TYPE } from '../enums/delivery-detail.enum';

export function computeRowForOneDeliveryDetail(bl, item, features, client_id) {
  let deliveryArray = [];

  /*
    ! Quickfix afin de gérer le fait que pour les clients autres que Cooperl (qui utilisent également packaging SAC) n'ai pas l'affichage type "Coffre à sacs".
  */
  if (client_id !== 5) {
    return deliveryArray;
  }

  const finalizedStatus = [
    BL_STATUS.DELIVERED,
    BL_STATUS.PARTIALLY_DELIVERED,
    BL_STATUS.CANCELED,
  ];

  // ~ item is VRAC
  if (item.packaging === PACKAGING_TYPE.VRAC) {
    // ~ delivery_type is REPRISE
    if (bl.delivery_type == DELIVERY_DETAIL_TYPE.REPRISE) {
      deliveryArray.push({
        what: formatCompartments(item),
        where: retrieveSilosToRetake(item),
        where_substitute: null,
        checked: finalizedStatus.includes(bl.status),
        invert_arrow: true,
      });
    }

    if (
      bl.delivery_type == DELIVERY_DETAIL_TYPE.LIVRAISON ||
      bl.delivery_type === DELIVERY_DETAIL_TYPE['LIVRAISON-VIA-TRANSFERT-SILO']
    ) {
      const silos = returnSiloObjects(item, bl);

      silos.forEach((silo) => {
        deliveryArray.push({
          what: formatCompartments(item),
          where_object: silo,
          checked: finalizedStatus.includes(bl.status),
          invert_arrow: false,
        });
      });
    }

    if (bl.delivery_type == DELIVERY_DETAIL_TYPE['TRANSFERT-SILO']) {
      deliveryArray.push({
        what: retrieveSilosToReclaim(item),
        where: retrieveSilosToTransfer(item),
        where_substitute: checkHasPlanned(item) ? item?.silos_delivered : null,
        checked: finalizedStatus.includes(bl.status),
        invert_arrow: false,
      });
    }

    if (bl.delivery_type == DELIVERY_DETAIL_TYPE.TRANSVIDAGE) {
      deliveryArray.push({
        what: formatTransvidageCompartmentsTransferredIn(item),
        where: formatCompartments(item),
        where_substitute: null,
        checked: finalizedStatus.includes(bl.status),
        invert_arrow: true,
      });
    }

    if (bl.delivery_type == DELIVERY_DETAIL_TYPE.GARDER) {
      deliveryArray.push({
        what: `Stocké en`,
        where: formatCompartments(item),
        where_substitute: null,
        checked: finalizedStatus.includes(bl.status),
        invert_arrow: false,
      });
    }
  }

  // ~ item is in Coffre à sacs
  if (item.packaging === PACKAGING_TYPE['SAC']) {
    deliveryArray.push({
      what: `${item?.quantity ?? 0} sac${item?.quantity > 1 ? 's' : ''}`,
      where: PACKAGING_TYPE['COFFRE'],
      where_substitute: null,
      checked: finalizedStatus.includes(bl.status),
      invert_arrow: false,
    });
  }

  return deliveryArray;
}

function retrieveSilosToReclaim(item) {
  if (item?.additional_data?.silos_to_retake) {
    return `Silo ${item?.additional_data?.silos_to_retake?.join(', ')}`;
  } else if (item?.silo_to_reclaim) {
    return `Silo ${item?.silo_to_reclaim}`;
  }

  return 'Silo inconnu';
}

function retrieveSilosToTransfer(item) {
  if (item?.additional_data?.silos_to_deliver) {
    return `Silo ${item?.additional_data?.silos_to_deliver?.join(', ')}`;
  } else if (item?.silo_to_transfer) {
    return `Silo ${item?.silo_to_transfer}`;
  }

  return 'Silo inconnu';
}

function formatTransvidageCompartmentsTransferredIn(item) {
  if (item?.transferred_in_containers) {
    return `Case ${item?.transferred_in_containers?.split(',').join(', ')}`;
  }

  return 'Case (?)';
}

function formatCompartments(item) {
  if (item?.compartments) {
    return `Case ${item?.compartments?.split(',').join(', ')}`;
  }

  return 'Case (?)';
}

function returnSiloObjects(item, bl) {
  const itemTotalQuantity =
    item.quantity +
    item.sub_items?.reduce((acc, subItem) => acc + subItem.quantity, 0);

  let silosPlanned = [];

  if (item?.additional_data?.silos_to_deliver) {
    silosPlanned = item?.additional_data?.silos_to_deliver;
  } else if (item.silo_1 || item.silo_2 || item.silo_3) {
    silosPlanned = [item.silo_1, item.silo_2, item.silo_3].filter(Boolean);
  }

  let silosDeliveredObjects = [];

  if (item?.additional_data?.silos_delivered) {
    silosDeliveredObjects = item?.additional_data?.silos_delivered;
  }

  const silosShown = new Map();
  const siloObjects = [];

  /*
    Dans le cas où le BL n'est pas encore commencé, on retourne juste les silos planifiés.

    Autrement, on fait dans l'ordre (grâce aux données additionnelles dans item) : 

    1) Les silos qui étaient prévus, et qui ont bien été livrés. 
    2) Les silos qui étaient prévus mais qui n'ont pas été livrés (seront remplacés par les silos supplémentaires si il y'en a)
    3) Les silos qui n'étaient pas prévus mais qui ont été livrés (si il en reste suite au remplacement)

    Si il n'y a pas de donnée additionnelle, on fallback sur l'ancienne méthode, sans aucune quantité par silo.
  */

  if (bl.status === BL_STATUS.PLANNED) {
    const silosPlannedObjects = [];
    for (const silo of silosPlanned) {
      let siloObject = {
        text: `Silo ${silo}`,
        total_quantity: itemTotalQuantity,
        delivered_quantity: 0.0,
        is_replaced: false,
        is_delivered: false,
        is_planned: true,
      };
      silosPlannedObjects.push(siloObject);
    }
    return silosPlannedObjects;
  }

  const silosPlannedAndDelivered = silosPlanned.filter((silo) =>
    silosDeliveredObjects.find(
      (siloObject) =>
        siloObject.silo === silo && siloObject.volume_delivered > 0.0
    )
  );
  for (const silo of silosPlannedAndDelivered) {
    if (silosShown.has(silo)) {
      continue;
    }

    let siloObject = {
      text: `Silo ${silo}`,
      total_quantity: itemTotalQuantity,
      delivered_quantity:
        silosDeliveredObjects.find((siloObject) => siloObject.silo === silo)
          ?.volume_delivered || itemTotalQuantity,
      is_replaced: false,
      is_delivered: true,
      is_planned: true,
    };

    silosShown.set(silo, true);
    siloObjects.push(siloObject);
  }

  const silosPlannedAndNotDelivered = silosPlanned.filter(
    (silo) => !silosPlannedAndDelivered.includes(silo)
  );

  if (silosPlannedAndNotDelivered.length > 0) {
    silosPlannedAndNotDelivered.forEach((silo) => {
      silosShown.set(silo, true);
    });

    let siloObject = {
      text: `Silo ${silosPlannedAndNotDelivered?.join(', ')}`,
      total_quantity: itemTotalQuantity,
      delivered_quantity: 0.0,
      is_replaced: true,
      is_delivered: false,
      is_planned: true,
    };

    const silosThatCouldHaveReplaceThisOne = silosDeliveredObjects.filter(
      (siloObject) =>
        siloObject.volume_delivered > 0.0 &&
        !silosPlanned.includes(siloObject.silo)
    );

    if (silosThatCouldHaveReplaceThisOne.length > 0) {
      const siloMatchingIndexInDelivered = silosDeliveredObjects?.find(
        (siloObject) =>
          siloObject.silo === silosThatCouldHaveReplaceThisOne?.[0]?.silo
      );

      if (siloMatchingIndexInDelivered) {
        siloObject.replaced_by = {
          text: `Silo ${siloMatchingIndexInDelivered?.silo}`,
          total_quantity: itemTotalQuantity,
          delivered_quantity:
            siloMatchingIndexInDelivered?.volume_delivered || itemTotalQuantity,
          is_replaced: false,
          is_delivered: true,
          is_planned: false,
        };
        silosShown.set(siloMatchingIndexInDelivered?.silo, true);
      }
    }

    siloObjects.push(siloObject);
  }

  const additionalSilosDelivered = silosDeliveredObjects.filter(
    (siloObject) =>
      !silosPlanned.includes(siloObject.silo) &&
      !silosShown.has(siloObject.silo)
  );
  for (const siloObject of additionalSilosDelivered) {
    if (silosShown.has(siloObject.silo)) {
      continue;
    }

    siloObjects.push({
      text: `Silo ${siloObject.silo}`,
      total_quantity: itemTotalQuantity,
      delivered_quantity: siloObject.volume_delivered,
      is_replaced: false,
      is_delivered: true,
      is_planned: false,
    });
  }

  const silosDelivered = item?.silos_delivered?.split(',');
  if (silosDelivered && silosDelivered.length > 0) {
    for (const silo of silosDelivered) {
      if (silosShown.has(silo)) {
        continue;
      }

      siloObjects.push({
        text: `Silo ${silo}`,
        total_quantity: itemTotalQuantity,
        delivered_quantity: itemTotalQuantity,
        is_replaced: false,
        is_delivered: true,
        is_planned: true,
      });
    }
  }

  return siloObjects;
}

function retrieveSilosToRetake(item) {
  if (item?.additional_data?.silos_to_retake) {
    return `Silo ${item?.additional_data?.silos_to_retake?.join(', ')}`;
  } else if (item?.silo_to_reclaim) {
    return `Silo ${item?.silo_to_reclaim}`;
  }

  return 'Silo inconnu';
}

function checkHasPlanned(item) {
  if (item?.additional_data?.silos_scanned) {
    const silosScanned = item?.additional_data?.silos_scanned?.map(
      (siloObject) => siloObject.silo
    );

    const silosPlanned = item?.additional_data?.silos_to_deliver || [];

    const siloScannedWereAllPlanned =
      silosScanned?.every((silo) => silosPlanned.includes(silo)) || false;

    return siloScannedWereAllPlanned;
  } else if (item?.silos_delivered) {
    return (
      item.silos_delivered !== item.silo_1 &&
      item?.silos_delivered !== item.silo_2 &&
      item?.silos_delivered !== item.silo_3
    );
  }

  return false;
}
